import React, { useEffect } from "react";
import { Content } from "@iventis/translations";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { Body1, Header1, IventisTheme, styled } from "@iventis/styles";
import { FarnboroughSVG } from "./farnborough-svg";
import { SandwellSVG } from "./sandwell-svg";
import { RWASVG } from "./rwa-svg";
import { NECSVG } from "./nec-svg";
import { UtilitaSVG } from "./utilita-svg";
import { Container } from "./digital-twin-components";
import { DigitalTwinStatus } from "./digitial-twin-types";
import { PulsatingLoading, useProgressTimer } from "./pulsating-loading";

export interface DigitalTwinLoadingComponentProps {
    status?: DigitalTwinStatus;
}

/**
 * A loading component for the digital twin.
 */
export const DigitalTwinLoadingComponent: React.FunctionComponent<DigitalTwinLoadingComponentProps> = ({ status }) => {
    const [currentSVG, setCurrentSVG] = React.useState<number>(0);
    const translate = useIventisTranslate();

    const progress = useProgressTimer({
        status,
        isLoading: (status) => status === "starting",
        isComplete: (status) => status === "available",
        durationMs: 80_000,
    });

    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentSVG((prevSVG) => (prevSVG + 1) % 5);
        }, 20000);
        return () => clearInterval(interval);
    }, []);

    const ModelSVG = (() => {
        switch (currentSVG) {
            case 0:
                return <StyledFarnboroughSVG />;
            case 1:
                return <StyledSandwellSVG />;
            case 2:
                return <StyledRWASVG />;
            case 3:
                return <StyledNECSVG />;
            case 4:
                return <StyledUtilitaSVG />;
            default:
                return <StyledFarnboroughSVG />;
        }
    })();

    return (
        <Container>
            {ModelSVG}
            <PulsatingHeading>{translate(Content.map8.digitalTwin.loading.header)}</PulsatingHeading>
            <PulsatingText>{translate(Content.map8.digitalTwin.loading.message)}</PulsatingText>
            {(status === "starting" || status === "available") && <PulsatingLoading variant="determinate" value={Math.round(progress)} />}
        </Container>
    );
};

const PulsatingHeading = styled(Header1)`
    animation: pulsate 2s infinite;
    @keyframes pulsate {
        0% {
            opacity: 0.5;
        }
        50% {
            opacity: 1;
        }
        100% {
            opacity: 0.5;
        }
    }
`;

const PulsatingText = styled(Body1)`
    animation: pulsate 2s infinite;
    @keyframes pulsate {
        0% {
            opacity: 0.5;
        }
        50% {
            opacity: 1;
        }
        100% {
            opacity: 0.5;
        }
    }
`;

const DrawingAnimation = (name, offset) => `
    path {
        stroke-dasharray: ${offset};
        stroke-dashoffset: -${offset};
        animation: ${name} 20s ease-out infinite;
    }
    @keyframes ${name} {
        0% {
            stroke-dashoffset: -${offset};
        }
        5% {
            stroke-dashoffset: -${offset};
        }
        85% {
            stroke-dashoffset: 0;
        }
        100% {
            stroke-dashoffset: 0;
        }
`;

const StyledFarnboroughSVG = styled(FarnboroughSVG)`
    flex: 1 1;
    max-width: 1024px;
    max-height: 512px;
    width: 100%;
    height: 100%;
    path {
        fill: none;
        stroke: ${({ theme }: { theme: IventisTheme }) => theme.typographyColors.primary};
        stroke-width: 0.2;
    }

    ${DrawingAnimation("Farnborough", 14000)}
`;

const StyledSandwellSVG = styled(SandwellSVG)`
    flex: 1 1;
    max-width: 1024px;
    max-height: 512px;
    width: 100%;
    height: 100%;
    path {
        fill: none;
        stroke: ${({ theme }: { theme: IventisTheme }) => theme.typographyColors.primary};
        stroke-width: 0.2;
    }
    ${DrawingAnimation("Sandwell", 6000)}
`;

const StyledRWASVG = styled(RWASVG)`
    flex: 1 1;
    max-width: 1024px;
    max-height: 512px;
    width: 100%;
    height: 100%;
    path {
        fill: none;
        stroke: ${({ theme }: { theme: IventisTheme }) => theme.typographyColors.primary};
        stroke-width: 0.2;
    }
    ${DrawingAnimation("RWA", 12000)}
`;

const StyledNECSVG = styled(NECSVG)`
    flex: 1 1;
    max-width: 1024px;
    max-height: 512px;
    width: 100%;
    height: 100%;
    path {
        fill: none;
        stroke: ${({ theme }: { theme: IventisTheme }) => theme.typographyColors.primary};
        stroke-width: 0.2;
    }
    ${DrawingAnimation("NEC", 6000)}
`;

const StyledUtilitaSVG = styled(UtilitaSVG)`
    flex: 1 1;
    max-width: 1024px;
    max-height: 512px;
    width: 100%;
    height: 100%;
    path {
        fill: none;
        stroke: ${({ theme }: { theme: IventisTheme }) => theme.typographyColors.primary};
        stroke-width: 0.2;
    }
    ${DrawingAnimation("Utilita", 10000)}
`;
